import { useMutation, useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import { Pass } from "ipctg-inflow-types/InFlowDB"
import { WelcomeFailReason } from "ipctg-inflow-types/InFlowEnums"
import { Trans, useTranslation } from "react-i18next"

import api from "@/utilities/api"

import { LinkButton } from "@/globalComponents/atoms/Button"
import Heading from "@/globalComponents/atoms/Heading"
import Icon from "@/globalComponents/atoms/Icon"
import Text from "@/globalComponents/atoms/Text"
import { PurchasedQuotaPackCard } from "@/globalComponents/molecules/productCards/PurchasedQuotaPack"
import { ParkWelcomeError } from "@/utilities/expectedErrors"
import { confirmParkPurchasePassQueryOptions } from "@/utilities/hooks/queries/park"
import { meQueryOptions } from "@/utilities/hooks/queries/user"
import { useErrorModal } from "@/utilities/hooks/useErrorModal"
import { formatPhoneNumberForDisplay } from "@/utilities/phone"
import welcomeTokenStorage from "@/utilities/WelcomeTokenStorage"

export const Route = createFileRoute(
  "/qr/$qrLookupID/passes/$epoch/purchase/confirmation"
)({
  staticData: {
    pageName: "Confirmation",
  },
  beforeLoad: async ({ context }) => {
    const cognitoIDToken = await api.auth.getCognitoIDToken()

    // Backend now handles idempotency via PAYMENTAPIRESPONSE table
    const purchaseData = await context.queryClient.fetchQuery(
      confirmParkPurchasePassQueryOptions(cognitoIDToken!)
    )

    return { purchaseData }
  },
  component: RouteComponent,
})

function RouteComponent() {
  const navigate = Route.useNavigate()
  const { qrLookupID } = Route.useParams()
  const { purchaseData } = Route.useRouteContext()
  const { data: me } = useSuspenseQuery(meQueryOptions())
  const { t } = useTranslation("passes")
  const { setError } = useErrorModal()

  const { mutate, isPending } = useMutation({
    mutationFn: async (_: { pass: Pass }) => {
      await api.welcome.POST({ qr: qrLookupID })
      const epoch = welcomeTokenStorage.getEpochTime()
      if (!epoch) {
        throw new ParkWelcomeError(WelcomeFailReason.SystemError)
      }
      return { epoch }
    },
    onSuccess: (data: { epoch: string }, variables: { pass: Pass }) => {
      navigate({
        to: "/qr/$qrLookupID/passes/$epoch/redeem/$passID",
        params: { epoch: data.epoch, passID: variables.pass.passID },
      })
    },
    onError: (error: Error) => setError({ error }),
  })

  // Get contact info (phone or email)
  const contactInfo = me?.cognitoUser?.phone_number
    ? formatPhoneNumberForDisplay(me?.cognitoUser?.phone_number)
    : me?.cognitoUser?.email || ""

  // Extract data from the purchase response
  const { receiptID, pass, canRedeemNow } = purchaseData

  // Show success state
  return (
    <>
      <Heading level={1} className="center">
        {t("confirmation.header")}
      </Heading>
      {purchaseData.canRedeemNow && (
        <Text level={1} className="center">
          {t("confirmation.description")}
        </Text>
      )}
      <section className="module">
        <PurchasedQuotaPackCard
          {...pass}
          action={canRedeemNow ? "redeem" : "warn"}
          isPending={isPending}
          onActionPressed={() => {
            mutate({ pass })
          }}
        />
        {contactInfo ? (
          <Text
            className="center"
            style={{ width: "100%", overflowWrap: "break-word" }}
          >
            <Trans
              ns="receipt"
              i18nKey="sentNotification"
              values={{ contact: contactInfo }}
            >
              Your receipt has been sent to: <br />
              <em className="semi-bold" style={{ wordBreak: "break-word" }}>
                {contactInfo}
              </em>
            </Trans>
          </Text>
        ) : (
          <></>
        )}
        <LinkButton
          buttonType="primary"
          presentation="filled"
          to="/qr/$qrLookupID/receipt/$receiptID"
          params={{ qrLookupID, receiptID }}
        >
          <Icon name="receipt" slot="left" />
          {t("viewReceipt", { ns: "receipt" })}
        </LinkButton>
      </section>
      <Link
        to="/qr/$qrLookupID/welcome"
        params={{ qrLookupID }}
        className="center semi-bold body--2"
      >
        {t("common.returnToPurchaseMenu", { ns: "common" })}
      </Link>
    </>
  )
}
